import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Star, ChevronLeft, ChevronRight, ShieldCheck, Users, Headphones } from 'lucide-react';
import { resolveReviewKey } from '../../constants/videoReviews';

/**
 * Google-style written reviews. Each entry has `keys` matching
 * canonical destination keys from videoReviews (manali, kashmir, etc.).
 */
const ALL_REVIEWS = [
  {
    name: 'Amit Parmar',
    rating: 4,
    text: 'I would like to sincerely thank Mountain Travel Chain for arranging such a wonderful and memorable trip. Everything was perfectly planned, from transportation and accommodation to sightseeing.',
    destination: 'Himachal Pradesh',
    keys: ['himachal', 'shimla', 'shimla-manali'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Destination_Images/Himachal.webp', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-06 at 12.44.52.jpeg', label: 'Day 4' },
    ],
    color: 'from-blue-500 to-blue-600',
  },
  {
    name: 'Rajashri Metkar',
    rating: 5,
    text: 'We appreciate the efforts taken by all the people who supported us throughout our trip. Mr. Sonam and Mr. Pravin were our Tour coordinators. They were continuously in touch with us.',
    destination: 'Ladakh',
    keys: ['leh-ladakh'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/Lehcity.jpg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/download (6).jpg', label: 'Day 3' },
    ],
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    name: 'Nandini',
    rating: 5,
    text: 'We had a wonderful experience with Mountain Chain Car Rental Services for Shimla Manali tour. The entire journey was smooth, comfortable, and very well managed.',
    destination: 'Shimla-Manali',
    keys: ['shimla-manali', 'shimla-manali-taxi', 'shimla', 'manali', 'himachal'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-05 at 15.20.34.jpeg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-05 at 17.32.15.jpeg', label: 'Day 3' },
    ],
    color: 'from-purple-500 to-purple-600',
  },
  {
    name: 'Nishchay Verma',
    rating: 5,
    text: "Mountain Chain Travel (MCT)'s 4x4 Winter Spiti Expedition: A Road Trip to Remember!",
    destination: 'Spiti Valley',
    keys: ['spiti-kinnaur', 'himachal'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/1604504948Kalpa Kinnaur Kailash Range.jpg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-09 at 13.05.15.jpeg', label: 'Day 4' },
    ],
    color: 'from-orange-500 to-red-500',
  },
  {
    name: 'Vaishali Verma',
    rating: 5,
    text: 'I had an amazing experience with Mountains Chain Travel. The entire trip was very well planned, from travel to stay. The team was extremely friendly, caring, and supportive.',
    destination: 'Himachal Pradesh',
    keys: ['himachal', 'shimla-manali'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2024-12-17 at 16.58.53.jpeg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2024-12-18 at 10.59.16.jpeg', label: 'Day 3' },
    ],
    color: 'from-pink-500 to-pink-600',
  },
  {
    name: 'Saikat Dasgupta',
    rating: 5,
    text: 'We just returned from a trip organized by Mountain Chain Travel, and it was very well organized. From the initial inquiry to the final drop-off, everything was seamless.',
    destination: 'Kashmir',
    keys: ['kashmir'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/Trip-To-Kashmir.jpg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/download (7).jpg', label: 'Day 3' },
    ],
    color: 'from-cyan-500 to-cyan-600',
  },
  {
    name: 'Manik',
    rating: 5,
    text: 'I recently traveled to Manali with Mountain Chain Travel Company, and overall, it was a good experience.',
    destination: 'Manali',
    keys: ['manali', 'himachal', 'shimla-manali'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-05 at 17.48.21.jpeg', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/WhatsApp Image 2025-01-05 at 17.32.15.jpeg', label: 'Day 2' },
    ],
    color: 'from-amber-500 to-amber-600',
  },
  {
    name: 'Sayani Chatterjee Ray',
    rating: 5,
    text: 'We had a wonderful family trip to Dharamshala & Dalhousie, Himachal Pradesh, very well planned and organized by Mountain Chains and Travels. This is 2nd time we are traveling with them.',
    destination: 'Dharamshala-Dalhousie',
    keys: ['dharamshala', 'himachal'],
    images: [
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/Places-To-Visit-In-Dalhousie.webp', label: 'Start' },
      { url: '/images/Dashboard/Assets/Images/Upcoming_packages/download (2).jpg', label: 'Day 3' },
    ],
    color: 'from-indigo-500 to-indigo-600',
  },
];

/** Resolve written reviews for a page slug / destination key. */
export function getTextReviews(slugOrKey) {
  const key = resolveReviewKey(slugOrKey) || slugOrKey;
  if (!key) return ALL_REVIEWS;
  const filtered = ALL_REVIEWS.filter((r) => r.keys.includes(key));
  return filtered.length > 0 ? filtered : ALL_REVIEWS;
}

export default function ReviewCards({ destinationKey, reviews: reviewsProp }) {
  const reviews = useMemo(() => {
    if (reviewsProp && reviewsProp.length > 0) return reviewsProp;
    if (destinationKey) return getTextReviews(destinationKey);
    return ALL_REVIEWS;
  }, [destinationKey, reviewsProp]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const reviewsPerView = Math.min(2, reviews.length);
  const maxIndex = Math.max(0, Math.ceil(reviews.length / reviewsPerView) - 1);

  // Reset carousel when destination reviews change
  useEffect(() => {
    setCurrentIndex(0);
  }, [destinationKey, reviews]);

  useEffect(() => {
    if (maxIndex <= 0) return undefined;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [maxIndex, reviews]);

  const next = () => {
    if (maxIndex <= 0) return;
    clearInterval(intervalRef.current);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);
  };

  const prev = () => {
    if (maxIndex <= 0) return;
    clearInterval(intervalRef.current);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);
  };

  const visibleReviews = reviews.slice(
    currentIndex * reviewsPerView,
    currentIndex * reviewsPerView + reviewsPerView
  );

  if (!reviews.length) return null;

  return (
    <section className="py-10 bg-brand-blue">
      <div className="max-w-7xl mx-auto px-6">
        {/* Trust Badges - White stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {[
            { icon: <Star size={16} className="text-brand-orange fill-brand-orange" />, label: '4.8 Google Rating', value: '2000+ Reviews' },
            { icon: <Users size={16} className="text-brand-orange" />, label: '3+ Lakh Travelers', value: 'Since 2015' },
            { icon: <ShieldCheck size={16} className="text-brand-orange" />, label: 'Govt Approved', value: 'Ministry of Tourism' },
            { icon: <Headphones size={16} className="text-brand-orange" />, label: '24x7 Support', value: 'Real Humans' },
          ].map((b, i) => (
            <div key={i} className="bg-white rounded-2xl px-3 py-3 sm:px-4 text-center shadow-md">
              <div className="font-poppins font-extrabold text-brand-blue text-[13px] sm:text-[14px] lg:text-[15px] leading-tight">{b.label}</div>
              <div className="text-brand-blue/60 text-[10px] sm:text-[11px] font-medium mt-0.5">{b.value}</div>
              <div className="mt-1.5 w-7 h-7 mx-auto rounded-full bg-brand-orange/10 flex items-center justify-center">
                {b.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Reviews Section */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-orange/20 rounded-full mb-2">
              <span className="text-brand-orange text-[11px] font-bold uppercase tracking-wider">Google Reviews</span>
            </div>
            <h2 className="font-poppins text-[26px] lg:text-[32px] text-white font-bold leading-[1.1]">What Our <span className="text-brand-orange">Travelers Say</span></h2>
          </div>
          {maxIndex > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prev}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={next}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Review Cards */}
        <div className={`grid grid-cols-1 gap-4 ${visibleReviews.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1 max-w-2xl'}`}>
          {visibleReviews.map((review, i) => (
            <div key={`${review.name}-${currentIndex}-${i}`} className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <div className="grid grid-cols-2 gap-1 p-1">
                {review.images.map((img, j) => (
                  <div key={j} className="relative rounded-xl overflow-hidden h-40 sm:h-48">
                    <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                    <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md">
                      <span className="text-white text-[10px] font-medium">{img.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} size={12} className="text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1 h-10 bg-brand-orange rounded-full shrink-0 mt-0.5" />
                  <div>
                    <p className="text-brand-blue text-[13px] leading-relaxed">{review.text}</p>
                    <div className="flex items-center gap-2 mt-2.5">
                      <div className={`w-7 h-7 rounded-full bg-gradient-to-r ${review.color} flex items-center justify-center text-white text-[10px] font-bold`}>
                        {review.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div className="text-brand-blue text-[12px] font-semibold">{review.name}</div>
                        <div className="text-brand-blue/50 text-[10px]">{review.destination}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Dots */}
        {maxIndex > 0 && (
          <div className="flex justify-center gap-1.5 mt-6">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                type="button"
                key={i}
                onClick={() => {
                  clearInterval(intervalRef.current);
                  setCurrentIndex(i);
                  intervalRef.current = setInterval(() => {
                    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
                  }, 4000);
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'w-6 bg-brand-orange' : 'w-1.5 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
